import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ChatService from "../../../Services/ChatService";
import SocketService from "../../../Services/SocketService";
import DataParser from "../../../utlits/Dataparser";

const ChatContext = createContext(null);

// ── Helpers ────────────────────────────────────────────────────────────────────
const _getImgUrl = (img) => {
  if (!img || img === "null" || img === "undefined") return null;
  if (img.startsWith("http")) return img;
  const base = (process.env.REACT_APP_BASE_URL || "http://localhost:5026/api/v1").split("/api/v1")[0];
  if (img.includes("/")) {
    const n = img.startsWith("/") ? img : `/${img}`;
    return `${base}${n.startsWith("/public") ? n : `/public${n}`}`;
  }
  return (process.env.REACT_APP_USER_DOCS_PATH || `${base}/public/users/docs/`) + img;
};

const _msgPreview = (data) => {
  if (!data) return "New message";
  if (data.type === "audio") return "🎤 Voice message";
  if (data.attachments?.length)
    return `📎 ${data.attachments[0].originalName || data.attachments[0].name || "Attachment"}`;
  return data.text || data.content || "New message";
};

// ── Hidden-rooms helpers (localStorage, per-user soft delete) ─────────────────
const _hiddenKey = (userId) => `hidden_chat_rooms_${userId}`;

const _getHidden = (userId) => {
  try { return JSON.parse(localStorage.getItem(_hiddenKey(userId)) || "[]"); }
  catch { return []; }
};

const _addHidden = (userId, roomId) => {
  const list = _getHidden(userId);
  if (!list.includes(String(roomId))) {
    localStorage.setItem(_hiddenKey(userId), JSON.stringify([...list, String(roomId)]));
  }
};

const _removeHidden = (userId, roomId) => {
  const list = _getHidden(userId);
  localStorage.setItem(_hiddenKey(userId), JSON.stringify(list.filter(id => id !== String(roomId))));
};

const _filterHidden = (rooms, userId) => {
  const hidden = _getHidden(String(userId));
  return rooms.filter(r => !hidden.includes(String(r._id)));
};

// ── Provider ───────────────────────────────────────────────────────────────────
export const ChatProvider = ({ children }) => {
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const [isNewDmMode, setIsNewDmMode] = useState(false);
  const [isNewChannelMode, setIsNewChannelMode] = useState(false);
  const [isInboxOpen, setIsInboxOpen] = useState(true);
  const [isChannelsOpen, setIsChannelsOpen] = useState(true);

  const activeRoomRef   = useRef(null);
  const roomsRef        = useRef([]);
  const isModalOpenRef  = useRef(false);
  const openChatRef     = useRef(null);   // stable ref so socket closure can call latest openChat
  const navigateRef     = useRef(null);   // stable ref so socket closure can call latest navigate

  // ── Notification-click safe-open ─────────────────────────────────────────────
  // If the user is on the login/auth page when a notification is clicked,
  // store the room ID and open it after login + navigation.
  const PENDING_NOTIF_ROOM = "planit_pending_notif_room";
  const _authRoutes = ['/', '/forgot-password', '/initial-reset-password'];
  const _isAuthRoute = (p) => _authRoutes.includes(p) || p.startsWith('/reset-password');

  const safeOpenChat = (room) => {
    if (!room) return;
    const livePath = window.location.pathname;
    const liveId   = DataParser.GetLoggedInUser()?.user?.userdata?.id;
    if (liveId && !_isAuthRoute(livePath)) {
      if (openChatRef.current) openChatRef.current(room);
    } else {
      // Not safe — park room ID; ChatModal will open it after login
      localStorage.setItem(PENDING_NOTIF_ROOM, String(room._id));
    }
  };

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { activeRoomRef.current  = activeRoom;  }, [activeRoom]);
  useEffect(() => { roomsRef.current       = rooms;       }, [rooms]);
  useEffect(() => { isModalOpenRef.current = isModalOpen; }, [isModalOpen]);

  // Close the chat modal whenever the user navigates to a different page
  useEffect(() => {
    if (isModalOpenRef.current) {
      setIsModalOpen(false);
    }
  }, [location.pathname]);

  // ── Service worker registration + notification permission ────────────────────
  const swRegistrationRef = useRef(null);

  useEffect(() => {
    // Register the service worker for reliable notifications (especially macOS)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then(reg => { swRegistrationRef.current = reg; })
        .catch(err => console.warn('[SW] Registration failed — notifications will use new Notification() fallback:', err));
    }

    // Request notification permission on first user click/key (browsers require a gesture).
    // We fire on the VERY NEXT interaction so users get the prompt quickly.
    if ("Notification" in window && Notification.permission === "default") {
      let permRequested = false;
      const requestOnInteraction = () => {
        if (permRequested) return;
        permRequested = true;
        Notification.requestPermission().then(result => {
          console.log('[Notif] Permission result:', result);
        }).catch(() => {});
        document.removeEventListener("click", requestOnInteraction, { capture: true, once: true });
        document.removeEventListener("keydown", requestOnInteraction, { capture: true, once: true });
      };
      document.addEventListener("click", requestOnInteraction, { capture: true, once: true });
      document.addEventListener("keydown", requestOnInteraction, { capture: true, once: true });
    }

    // Handle chat notification clicks from the header notification panel
    const handleOpenChatRoom = (e) => {
      const { roomId } = e.detail;
      const r = roomsRef.current.find(rm => String(rm._id) === String(roomId));
      if (r) safeOpenChat(r);
    };
    window.addEventListener("openChatRoom", handleOpenChatRoom);

    // Handle notification clicks posted from the service worker
    const handleSwMessage = (event) => {
      if (event.data?.type !== 'CHAT_NOTIFICATION_CLICK') return;
      const { roomId, url } = event.data;
      const r = roomsRef.current.find(rm => String(rm._id) === String(roomId));
      if (url && navigateRef.current) {
        navigateRef.current(url);
      }
      if (r) {
        setTimeout(() => safeOpenChat(r), 200);
      } else if (roomId) {
        localStorage.setItem(PENDING_NOTIF_ROOM, String(roomId));
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleSwMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSwMessage);
      window.removeEventListener("openChatRoom", handleOpenChatRoom);
    };
  }, []);

  // ── Socket handlers + room bootstrap ────────────────────────────────────────
  useEffect(() => {
    const currentUser = DataParser.GetLoggedInUser();
    if (!currentUser?.user?.userdata) return;

    const userId = currentUser.user.userdata.id;

    // Connect the socket here (provider always mounts) so the receiver is
    // always connected, not just when they open the chat modal.
    SocketService.connect();
    SocketService.emit("registerUser", String(userId));

    // Re-join every known room after each (re)connect so no room is missed,
    // then refresh the room list to catch any rooms/messages added while offline
    const handleConnect = () => {
      SocketService.emit("registerUser", String(userId));
      roomsRef.current.forEach(r => SocketService.emit("joinChatRoom", r._id));
      ChatService.getRooms()
        .then(res => {
          if (!res.Good) return;
          const visible = _filterHidden(res.rooms, String(userId));
          setRooms(prev => {
            const roomMap = new Map(prev.map(r => [String(r._id), r]));
            const merged = visible.map(r => {
              const existing = roomMap.get(String(r._id));
              if (!existing) {
                SocketService.emit("joinChatRoom", r._id);
                return r;
              }
              // Keep whichever unread count is higher so in-flight socket increments aren't lost
              return { ...r, unreadCount: Math.max(r.unreadCount || 0, existing.unreadCount || 0) };
            });
            return merged;
          });
        })
        .catch(() => {});
    };
    SocketService.on("connect", handleConnect);

    ChatService.getRooms()
      .then(res => {
        if (res.Good) {
          const visible = _filterHidden(res.rooms, userId);
          setRooms(prev => {
            const prevMap = new Map(prev.map(r => [String(r._id), r]));
            const serverIds = new Set(visible.map(r => String(r._id)));
            const localOnly = prev.filter(r => !serverIds.has(String(r._id)));
            // For existing rooms, keep the higher unreadCount so socket-incremented
            // counts are never overwritten by a stale server snapshot
            const merged = visible.map(r => {
              const existing = prevMap.get(String(r._id));
              return existing
                ? { ...r, unreadCount: Math.max(r.unreadCount || 0, existing.unreadCount || 0) }
                : r;
            });
            const all = [...merged, ...localOnly];
            all.forEach(r => SocketService.emit("joinChatRoom", r._id));
            return all;
          });
        }
      })
      .catch(() => {});

    const notificationSound = new Audio(
      "https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3"
    );
    const currentUserId = currentUser.user.userdata.id;

    const handleNewRoom = (room) => {
      if (_getHidden(String(currentUserId)).includes(String(room._id))) return;
      SocketService.emit("joinChatRoom", room._id);
      setRooms(prev => {
        if (prev.find(r => r._id === room._id)) return prev;
        return [room, ...prev];
      });
    };

    const handleNewMessage = (data) => {
      // Silently ignore messages from rooms the user has hidden (deleted from their view)
      const hiddenRooms = _getHidden(String(currentUserId));
      if (hiddenRooms.includes(String(data.room_id))) return;

      if (String(data.sender_id) !== String(currentUserId)) {
        const dataRoomId = String(data.room_id);
        const room = roomsRef.current.find(r => String(r._id) === dataRoomId);
        const isMuted = room?.is_muted;

        // Suppress sound for muted rooms
        if (!isMuted) notificationSound.play().catch(() => {});

        // Always ACK delivery from ChatContext (always mounted, not just when modal is open)
        if (data._id) {
          SocketService.emit("messageDelivered", {
            messageId: data._id,
            senderId: data.sender_id,
            roomId: data.room_id,
          });
        }

        // Only notify when not already looking at this room and not muted
        const isViewing =
          isModalOpenRef.current && String(activeRoomRef.current?._id) === dataRoomId;

        if (!isViewing && !isMuted && "Notification" in window && Notification.permission === "granted") {
          const room = roomsRef.current.find(r => String(r._id) === dataRoomId);
          const roomLabel = room?.type === "channel" || room?.type === "project"
            ? `# ${room.name}` : null;

          const title = roomLabel
            ? `${data.sender_name || "New message"} · ${roomLabel}`
            : data.sender_name || "New message";

          const cid = DataParser.CompanyIdentifier();

          // ── Simple, reliable notification ──────────────────────────────────
          // Do NOT include icon — a broken/auth-blocked image URL silently kills
          // the entire notification in Chrome, Firefox and Safari.
          // Use a unique tag so each message gets its own popup (no suppression).
          try {
            const osNotif = new Notification(title, {
              body: _msgPreview(data),
              tag: `chat-${data._id || dataRoomId}-${Date.now()}`,
            });
            osNotif.onclick = () => {
              osNotif.close();
              window.focus();
              const r = roomsRef.current.find(rm => String(rm._id) === dataRoomId);
              if (navigateRef.current && cid) {
                navigateRef.current(`/${cid}/employee-attendance`);
              }
              setTimeout(() => safeOpenChat(r || { _id: dataRoomId }), 500);
            };
            setTimeout(() => osNotif.close(), 8000);
          } catch (e) {
            console.warn('[Notif] new Notification() failed:', e);
          }
        }

        // ── In-app notification → send to header notification panel ──────────
        // Only when not actively viewing that room.
        if (!isViewing) {
          const notifRoom = roomsRef.current.find(r => String(r._id) === dataRoomId);
          const isChannel = notifRoom?.type === "channel" || notifRoom?.type === "project";
          const notifTitle = isChannel
            ? `#${notifRoom?.name || "Channel"}`
            : data.sender_name || "New message";
          const notifBody = isChannel
            ? `${data.sender_name}: ${_msgPreview(data)}`
            : _msgPreview(data);

          // Dispatch a custom event so Notofication.jsx can pick it up without coupling
          window.dispatchEvent(new CustomEvent("chatNotification", {
            detail: {
              id: `chat-${dataRoomId}-${Date.now()}`,
              roomId: dataRoomId,
              title: notifBody.length > 60 ? notifBody.slice(0, 60) + "…" : notifBody,
              senderName: data.sender_name || "",
              senderImage: data.sender_image || null,
              createdAt: new Date().toISOString(),
              type: "chat",
            }
          }));
        }
      }

      setRooms(prev => {
        const dataRoomId = String(data.room_id);
        const roomExists = prev.find(r => String(r._id) === dataRoomId);
        if (!roomExists) {
          // Join the socket room immediately so future messages aren't missed
          SocketService.emit("joinChatRoom", data.room_id);
          ChatService.getRooms()
            .then(res => {
              if (res.Good) {
                const visible = _filterHidden(res.rooms, currentUserId);
                setRooms(prev2 => {
                  const prevMap = new Map(prev2.map(r => [String(r._id), r]));
                  const serverIds = new Set(visible.map(r => String(r._id)));
                  const localOnly = prev2.filter(r => !serverIds.has(String(r._id)));
                  const isViewing = isModalOpenRef.current && String(activeRoomRef.current?._id) === dataRoomId;
                  const merged = visible.map(r => {
                    const existing = prevMap.get(String(r._id));
                    const base = existing
                      ? { ...r, unreadCount: Math.max(r.unreadCount || 0, existing.unreadCount || 0) }
                      : r;
                    // Always stamp the triggering room with the new message + bump count
                    if (String(r._id) === dataRoomId) {
                      return { ...base, last_message: data, unreadCount: isViewing ? 0 : (base.unreadCount || 0) + 1, force_unread: isViewing ? false : base.force_unread };
                    }
                    return base;
                  });
                  merged.forEach(r => SocketService.emit("joinChatRoom", r._id));
                  return [...merged, ...localOnly];
                });
              }
            })
            .catch(() => {});
          return prev;
        }
        return prev.map(r => {
          if (String(r._id) !== dataRoomId) return r;
          const isViewing = isModalOpenRef.current && String(activeRoomRef.current?._id) === dataRoomId;
          return {
            ...r,
            last_message: data,
            unreadCount: isViewing ? 0 : (r.unreadCount || 0) + 1,
          };
        });
      });
    };

    const handleProjectUpdate = (data) => {
      if (data.action === "create") {
        setTimeout(() => {
          ChatService.getRooms()
            .then(res => {
              if (res.Good) {
                const visible = _filterHidden(res.rooms, currentUserId);
                setRooms(prev => {
                  const prevMap = new Map(prev.map(r => [String(r._id), r]));
                  const serverIds = new Set(visible.map(r => String(r._id)));
                  const localOnly = prev.filter(r => !serverIds.has(String(r._id)));
                  const merged = visible.map(r => {
                    const existing = prevMap.get(String(r._id));
                    return existing
                      ? { ...r, unreadCount: Math.max(r.unreadCount || 0, existing.unreadCount || 0) }
                      : r;
                  });
                  return [...merged, ...localOnly];
                });
              }
            })
            .catch(() => {});
        }, 2000);
      }
    };

    const handleRoomDeleted = (data) => {
      const deletedId = String(data.roomId || data.room_id);
      setRooms(prev => prev.filter(r => String(r._id) !== deletedId));
      if (String(activeRoomRef.current?._id) === deletedId) {
        setActiveRoom(null);
        setIsModalOpen(false);
      }
    };

    const handleUserLeftRoom = (data) => {
      setRooms(prev => prev.map(r =>
        String(r._id) === String(data.roomId)
          ? { ...r, members: (r.members || []).filter(m => String(m.id || m._id) !== String(data.userId)) }
          : r
      ));
    };

    // Mute / Mark-as-unread / Clear-unread — synced across all tabs for the same user
    const handleRoomSettingUpdated = ({ roomId, type, is_muted }) => {
      setRooms(prev => prev.map(r => {
        if (String(r._id) !== String(roomId)) return r;
        if (type === "mute")         return { ...r, is_muted };
        if (type === "force_unread") return { ...r, unreadCount: Math.max(r.unreadCount || 0, 1), force_unread: true };
        if (type === "clear_unread") return { ...r, unreadCount: 0, force_unread: false };
        return r;
      }));
    };

    SocketService.on("newRoom",                 handleNewRoom);
    SocketService.on("receiveChatMessage",      handleNewMessage);
    SocketService.on("project_update",          handleProjectUpdate);
    SocketService.on("roomDeleted",             handleRoomDeleted);
    SocketService.on("userLeftRoom",            handleUserLeftRoom);
    SocketService.on("chatRoomSettingUpdated",  handleRoomSettingUpdated);

    return () => {
      SocketService.off("connect",                handleConnect);
      SocketService.off("newRoom",                handleNewRoom);
      SocketService.off("receiveChatMessage",     handleNewMessage);
      SocketService.off("project_update",         handleProjectUpdate);
      SocketService.off("roomDeleted",            handleRoomDeleted);
      SocketService.off("userLeftRoom",           handleUserLeftRoom);
      SocketService.off("chatRoomSettingUpdated", handleRoomSettingUpdated);
    };
  }, []);

  const openChat = (room, rect = null) => {
    setActiveRoom(room);
    setIsModalOpen(true);
    setIsNewDmMode(false);
    if (rect) setAnchorRect(rect);
    if (room?._id) {
      setRooms(prev =>
        prev.map(r => r._id === room._id ? { ...r, unreadCount: 0 } : r)
      );
      // Expand the correct sidebar section for this room type
      if (room.type === "channel" || room.type === "project") {
        setIsChannelsOpen(true);
      } else if (room.type === "direct") {
        setIsInboxOpen(true);
      }
    }
  };

  const openNewDm = (rect = null) => {
    setActiveRoom(null);
    setIsModalOpen(true);
    setIsNewDmMode(true);
    setIsNewChannelMode(false);
    if (rect) setAnchorRect(rect);
  };

  const openNewChannel = (rect = null) => {
    setActiveRoom(null);
    setIsModalOpen(true);
    setIsNewChannelMode(true);
    setIsNewDmMode(false);
    if (rect) setAnchorRect(rect);
  };

  // Keep refs current so socket closures always call the latest versions
  openChatRef.current  = openChat;
  navigateRef.current  = navigate;

  const closeChat = () => {
    setIsModalOpen(false);
    setActiveRoom(null);
    setIsNewDmMode(false);
    setIsNewChannelMode(false);
  };

  const refreshRooms = () => {
    const userId = DataParser.GetLoggedInUser()?.user?.userdata?.id;
    ChatService.getRooms()
      .then(res => {
        if (res.Good) {
          const visible = _filterHidden(res.rooms, String(userId));
          setRooms(prev => {
            const prevMap = new Map(prev.map(r => [String(r._id), r]));
            const serverIds = new Set(visible.map(r => String(r._id)));
            const localOnly = prev.filter(r => !serverIds.has(String(r._id)));
            const merged = visible.map(r => {
              const existing = prevMap.get(String(r._id));
              return existing
                ? { ...r, unreadCount: Math.max(r.unreadCount || 0, existing.unreadCount || 0) }
                : r;
            });
            const all = [...merged, ...localOnly];
            all.forEach(r => SocketService.emit("joinChatRoom", r._id));
            return all;
          });
        }
      })
      .catch(() => {});
  };

  const hideConversation = (roomId) => {
    const userId = DataParser.GetLoggedInUser()?.user?.userdata?.id;
    if (userId) _addHidden(String(userId), String(roomId));
    // Persist to DB so getOrCreateRoom skips this room for a fresh start
    ChatService.hideRoom(roomId).catch(() => {});
    setRooms(prev => prev.filter(r => String(r._id) !== String(roomId)));
    if (activeRoom?._id === roomId) {
      setActiveRoom(null);
      setIsModalOpen(false);
    }
  };

  const unhideConversation = (roomId) => {
    const userId = DataParser.GetLoggedInUser()?.user?.userdata?.id;
    if (userId) _removeHidden(String(userId), String(roomId));
  };

  return (
    <ChatContext.Provider value={{
      rooms, setRooms,
      activeRoom, setActiveRoom,
      isModalOpen, anchorRect, setAnchorRect,
      isNewDmMode, setIsNewDmMode,
      isNewChannelMode, setIsNewChannelMode,
      isInboxOpen, setIsInboxOpen,
      isChannelsOpen, setIsChannelsOpen,
      openChat, closeChat, openNewDm, openNewChannel,
      refreshRooms,
      hideConversation,
      unhideConversation,
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => useContext(ChatContext);
export default ChatContext;
